import React, { useState, useEffect, useContext } from 'react';
import { getReportFromId, ScrapbookFile } from '@/api/scrapbook-data';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import XLight from '@/assets/xLight.svg';
import Copy from '@/assets/copy-simple-light.svg';
import ReportCard from '@/components/report/ReportCard';
import { ReportSearchModel } from '@/types';
import {
  UserContext,
  Modal,
  AppInsightsContext,
  MetadataContext,
  TrackEventName,
  TrackEventKey,
  TrackEventType,
  EntityCardSize,
} from '@/components';
import Tooltip from '@/components/general/Tooltip';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

interface ContentPreviewWraperProps {
  content: ScrapbookFile | null;
  children?: React.ReactNode;
  title: string;
  subTitle?: string;
  className?: string;
  onDownload?: () => void;
  onCopy?: () => void;
  onClose: () => void;
  footerContent?: React.ReactNode;
}
const ContentPreviewWraper = ({
  content,
  onClose,
  children,
  title,
  subTitle,
  onDownload,
  onCopy,
  className,
  footerContent,
}: ContentPreviewWraperProps) => {
  const [showCopied, setShowCopied] = useState(false);
  const [report, setReport] = useState<ReportSearchModel | null>(null);

  const t = useTranslations();
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);
  const router = useRouter();

  useEffect(() => {
    const fetchReportData = async () => {
      const reportId = Number(content?.content?.pageId);
      const result = await getReportFromId(reportId, user.account, user.userId);

      if (result && Object.keys(result).length > 0) {
        setReport(result);
      }
    };

    if (content && !content.isBlob && content.content?.pageId && user?.account && user?.userId) {
      fetchReportData();
    }
  }, [content, user?.account, user?.userId]);

  const _onCopy = () => {
    if (onCopy) {
      onCopy();
      setShowCopied(true);
      setTimeout(() => {
        setShowCopied(false);
      }, 2000);
    }
  };

  const _onClose = () => {
    onClose();
    setTimeout(() => {
      setReport(null);
    }, 200);
  };

  const onReportCardClick = (e: React.MouseEvent, data: ReportSearchModel) => {
    appInsights.trackCustomEvent(TrackEventName.STAT_BANK, TrackEventKey.STAT_REPORT, TrackEventType.SELECTED, user, {
      pageId,
      pagePath: router.asPath,
      reportID: data.reportId,
    });
  };

  return (
    <Modal
      open={!!content}
      onClose={_onClose}
      contanerClassName={classNames('max-h-4/5 max-w-4/5 bg-white z-[60]', className)}
    >
      <div className="flex h-full w-full flex-col rounded-md">
        {/* Header */}
        <div className="v-card__title flex-shrink-0 !items-start">
          <div className="headline">
            <h3 className="contentType" style={{ fontSize: '28px' }}>
              {title}
            </h3>
            {subTitle && <div>{subTitle}</div>}
          </div>
          <div className="spacer flex-1"></div>
          <button type="button" className="btn icon" onClick={_onClose}>
            <XLight height="28" width="28" />
          </button>
        </div>

        <hr role="separator" aria-orientation="horizontal" className="border-t border-gray-200" />

        {/* Content */}
        <div className="content-wrapper flex w-full flex-1 gap-3 overflow-y-auto px-4 py-3" data-lenis-prevent>
          <div className={classNames('px-4', report ? 'w-3/4 border-r-2 border-gray-200' : 'w-full')}>{children}</div>
          {report && (
            <div className="w-1/4">
              <ReportCard
                data={report}
                hideTitle={true}
                hideTopContent={true}
                onClick={onReportCardClick}
                size={EntityCardSize.AUTO}
              />
            </div>
          )}
        </div>

        {/* Copy/Download Button */}
        {(onDownload || onCopy) && (
          <div className="v-card__actions flex flex-shrink-0 items-center justify-between rounded-b-lg bg-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              {onDownload && (
                <Tooltip content={t('general.$download')} placement="top">
                  <button className="btn icon !bg-transparent" onClick={onDownload}>
                    <DownloadSimpleLight height="28" width="28" />
                  </button>
                </Tooltip>
              )}
              {onCopy && (
                <>
                  <Tooltip content={t('general.$copytoclipboard')} placement="top">
                    <button className="btn icon !bg-transparent" onClick={_onCopy}>
                      <Copy height="28" width="28" />
                    </button>
                  </Tooltip>
                  {showCopied && <div className="ml-3 text-sm font-medium text-black"> {t('general.$copied')}</div>}
                </>
              )}
            </div>
            <div>{footerContent}</div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ContentPreviewWraper;
